import { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGES, Shell, type PageId } from './components/layout/Shell';
import { Overview } from './pages/Overview';
import { Platforms } from './pages/Platforms';
import { Engagement } from './pages/Engagement';
import { Features } from './pages/Features';
import { Stability } from './pages/Stability';
import { Releases } from './pages/Releases';
import { useSnapshot } from './hooks/useSnapshot';
import { LoginGate } from './components/layout/LoginGate';
import { clearSession, getSession, renewIfStale, type Session } from './data/auth';
import { isLiveConfigured } from './data/api';
import { latestVersions } from './lib/platform';
import { DEFAULT_RANGE, isPlatformFilter, isRangeKey } from './data/types';
import type { PlatformFilter, RangeKey } from './data/types';

type Theme = 'dark' | 'light';

const THEME_KEY = 'pyraxis-dashboard-theme';
const VIEW_KEY = 'pyraxis-dashboard-view';

/**
 * The header controls survive a reload.
 *
 * Reading a report is rarely a one-page job — filtering to macOS, reloading and
 * landing back on "All platforms / Overview" silently changes what the numbers
 * mean, and it is easy not to notice. Everything restored is validated against
 * the current options, so a stale value (a range key from before the rename, a
 * platform that no longer exists) falls back to the default instead of asking
 * the service for something it will reject.
 */
interface View {
  page: PageId;
  range: RangeKey;
  platform: PlatformFilter;
}

const DEFAULT_VIEW: View = { page: 'overview', range: DEFAULT_RANGE, platform: 'all' };

function initialView(): View {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    if (!raw) return DEFAULT_VIEW;
    const saved = JSON.parse(raw) as Partial<View>;
    return {
      page: PAGES.some((p) => p.id === saved.page) ? (saved.page as PageId) : DEFAULT_VIEW.page,
      range: isRangeKey(saved.range) ? saved.range : DEFAULT_VIEW.range,
      platform: isPlatformFilter(saved.platform) ? saved.platform : DEFAULT_VIEW.platform,
    };
  } catch {
    return DEFAULT_VIEW; // unparseable, or storage disabled in private mode
  }
}

function initialTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Auth, and nothing else.
 *
 * The dashboard is a separate component rather than a branch inside this one,
 * so that it does not exist at all until there is a session: `useSnapshot`
 * starts polling the moment it mounts, and a hook cannot be skipped
 * conditionally. Mounting the whole thing behind the gate is what keeps a
 * signed-out tab from firing a 401 every thirty seconds.
 *
 * The theme lives up here too, so the sign-in screen is already in the right
 * one — a white flash on the way to a dark dashboard is a small thing that
 * reads as a bug.
 */
export default function App() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [session, setSession] = useState<Session | null>(getSession);
  /** Distinguishes "signed out by the service" from "never signed in". */
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Push the expiry out on load. A dashboard opened every week or so should
  // never reach the 30-day wall; one abandoned for a month should.
  useEffect(() => {
    if (!getSession()) return;
    void renewIfStale().then((next) => {
      if (next) setSession((current) => (current && next.token === current.token ? current : next));
    });
    // Once per mount: a keep-alive, not a reaction to state. Depending on
    // `session` here would re-run it on the state it sets.
  }, []);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  const signOut = useCallback(() => {
    clearSession();
    setExpired(false);
    setSession(null);
  }, []);

  /*
   * The service refused the token we hold — it lapsed, or AUTH_SECRET was
   * rotated out from under it. Drop it rather than leaving a dead session in
   * storage for the next reload to trip over.
   *
   * Stable identity: the dashboard calls this from an effect, and a new
   * function each render would re-run that effect on every poll.
   */
  const onRejected = useCallback(() => {
    clearSession();
    setExpired(true);
    setSession(null);
  }, []);

  if (isLiveConfigured && !session) {
    return (
      <LoginGate
        expired={expired}
        theme={theme}
        onTheme={toggleTheme}
        onSignedIn={(next) => {
          setExpired(false);
          setSession(next);
        }}
      />
    );
  }

  return (
    <Dashboard
      theme={theme}
      onTheme={toggleTheme}
      onSignOut={isLiveConfigured ? signOut : undefined}
      onRejected={onRejected}
    />
  );
}

interface DashboardProps {
  theme: Theme;
  onTheme: () => void;
  /** Absent on sample data — there is nothing to sign out of. */
  onSignOut?: () => void;
  onRejected: () => void;
}

function Dashboard({ theme, onTheme, onSignOut, onRejected }: DashboardProps) {
  const [view] = useState(initialView);
  const [page, setPage] = useState<PageId>(view.page);
  const [range, setRange] = useState<RangeKey>(view.range);
  const [platform, setPlatform] = useState<PlatformFilter>(view.platform);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify({ page, range, platform }));
    } catch {
      // Storage disabled — the dashboard still works, it just forgets.
    }
  }, [page, range, platform]);

  const { data, loading, error, refreshing, updatedAt, nextAt } = useSnapshot(range);

  // The sidebar's "Shipping" block. Derived from whatever the snapshot says is
  // current, so cutting a release moves it without anyone editing this file.
  const versions = useMemo(
    () => (data ? latestVersions(data.snapshot.versions) : { desktop: null, mobile: null }),
    [data],
  );

  // Handled in an effect rather than during render, because it unmounts this
  // component from the parent's state.
  const rejected = data?.source === 'unauthorized';
  useEffect(() => {
    if (rejected) onRejected();
  }, [rejected, onRejected]);

  return (
    <Shell
      page={page}
      onPage={setPage}
      range={range}
      onRange={setRange}
      platform={platform}
      onPlatform={setPlatform}
      theme={theme}
      onTheme={onTheme}
      onSignOut={onSignOut}
      versions={versions}
      source={data?.source ?? 'mock'}
      refreshing={refreshing}
      updatedAt={updatedAt}
      nextAt={nextAt}
    >
      {data?.source === 'fallback' ? (
        <div className="banner">
          <span className="dot" style={{ background: 'var(--status-critical)' }} />
          <span>
            Could not reach the telemetry API (<b>{data.error}</b>). Showing sample data so the layout stays
            usable.
          </span>
        </div>
      ) : null}

      {data?.source === 'mock' ? (
        <div className="banner banner__accent">
          <span className="dot" style={{ background: 'var(--status-warning)' }} />
          <span>
            <b>Sample data.</b> Both clients can emit telemetry (opt-in, off by default), but no ingest
            service is configured here. Run the one in <code>server/</code> and set{' '}
            <code>VITE_API_BASE_URL</code> to point at it — see <code>docs/INGEST_API.md</code>.
          </span>
        </div>
      ) : null}

      {error ? (
        <div className="banner">
          <span className="dot" style={{ background: 'var(--status-critical)' }} />
          <span>Failed to load: {error}</span>
        </div>
      ) : null}

      {loading && !data ? (
        <LoadingSkeleton />
      ) : data ? (
        <>
          {page === 'overview' ? <Overview snap={data.snapshot} filter={platform} /> : null}
          {page === 'platforms' ? <Platforms snap={data.snapshot} filter={platform} /> : null}
          {page === 'engagement' ? <Engagement snap={data.snapshot} filter={platform} /> : null}
          {page === 'features' ? <Features snap={data.snapshot} filter={platform} /> : null}
          {page === 'stability' ? <Stability snap={data.snapshot} filter={platform} /> : null}
          {page === 'releases' ? <Releases snap={data.snapshot} filter={platform} /> : null}
        </>
      ) : (
        <p className="empty">No data.</p>
      )}
    </Shell>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid" aria-busy="true" aria-label="Loading">
      <div className="skeleton" data-span style={{ gridColumn: 'span 4', height: 200 }} />
      <div className="skeleton" data-span style={{ gridColumn: 'span 8', height: 200 }} />
      <div className="skeleton" data-span style={{ gridColumn: 'span 8', height: 340 }} />
      <div className="skeleton" data-span style={{ gridColumn: 'span 4', height: 340 }} />
    </div>
  );
}
